import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { FiArrowRight, FiCheck, FiPlusCircle, FiShare2, FiFileText, FiUsers } from 'react-icons/fi';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { ShareEventModal } from '../components/events/ShareEventModal';
import { ManagePrivateAccessModal } from '../components/events/ManagePrivateAccessModal';
import { getEventCoverImage, useGetEventDetail } from '../api/events';
import planBcg from '../assets/plan-bcg.png';
import crewBcg from '../assets/crewbcg.png';
import { formatDate, formatTime } from '../utils/eventDate';
import { useTimezoneLabel } from '../api/timezones';
import { toast } from '../utils/toast';
import { getEventSuccess, saveGigPrefill } from '../utils/eventSuccess';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;

export const Route = createFileRoute('/events/$eventId/success')({
  beforeLoad: ({ params }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({ to: '/sign-in', search: { redirect: `/events/${params.eventId}` } });
    }
    // Nothing stored (direct visit / new tab) → fall back to the regular event page.
    if (!getEventSuccess(params.eventId)) {
      throw redirect({
        to: '/events/$eventId',
        params: { eventId: params.eventId },
        replace: true,
      });
    }
  },
  component: EventSuccess,
});

function EventSuccess() {
  const { eventId } = Route.useParams();
  const navigate = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const snapshot = getEventSuccess(eventId);
  const { data: event } = useGetEventDetail(eventId);
  const getTimezoneLabel = useTimezoneLabel();
  if (!snapshot) return null;

  // Same URL the event details page shares: prefer the fetched event's uuid.
  const shareIdentifier = event?.event_uuid || snapshot.eventUuid || event?.id || snapshot.eventId;
  const shareUrl = `${API_BASE_URL.replace(/\/+$/, '')}/embedded_checkout_page/${shareIdentifier}`;
  const shareTitle = event?.event_title || snapshot.title || 'Musicade Event';
  const coverImage = snapshot.imageUrl || (event ? getEventCoverImage(event) : null);
  const isPrivate = Boolean(event?.is_private);
  const isExternal =
    Boolean(snapshot.isExternal) ||
    event?.event_ticketing_type === 'external' ||
    Boolean(event?.external_ticketing_url);

  const goToEvent = () =>
    navigate({ to: '/events/$eventId', params: { eventId: snapshot.eventId } });

  const goToProject = () => {
    if (!snapshot.projectUuid) {
      toast.error('Project not found for this event.');
      return;
    }
    navigate({ to: '/projects/$projectId', params: { projectId: snapshot.projectUuid } });
  };

  const goToCreateGig = () => {
    saveGigPrefill(snapshot);
    navigate({ to: '/gigs/create', search: { fromEvent: snapshot.eventId } });
  };

  const whenLabel = snapshot.start
    ? `${formatDate(snapshot.start, snapshot.timezone, true)}  •  ${formatTime(
        snapshot.start,
        snapshot.timezone,
        true,
        getTimezoneLabel(snapshot.timezone, event?.timezone_label),
      )}`
    : null;

  return (
    <div className="min-h-screen flex flex-col bg-black text-white pb-32">
      <Header />

      <main className="flex-1 flex items-center w-full pt-28 md:pt-24 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="w-full grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-8 lg:gap-10 items-center">
          {/* Left: confirmation + event summary */}
          <section className="flex flex-col items-center text-center">
            <div className="w-24 h-24 rounded-full border border-green-900/60 bg-green-950/40 flex items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-green-400 flex items-center justify-center">
                <FiCheck size={28} className="text-black" strokeWidth={3} />
              </div>
            </div>
            <h1 className="mt-6 font-['Funnel_Display'] font-medium text-[32px] leading-none text-center">
              Your event is live! 🎉
            </h1>
            <p className="mt-3 font-['Outfit'] text-[14px] leading-none text-[#A3A3A3]">
              Keep the momentum going by completing the next steps for your event
            </p>

            <div className="mt-8 w-full flex overflow-hidden rounded-2xl border border-white/10 bg-zinc-950 text-left">
              <div className="relative w-28 sm:w-36 shrink-0 self-stretch min-h-40 bg-zinc-900">
                <img
                  src={coverImage || '/Musicade-Logo.png'}
                  alt={snapshot.title}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
              <div className="p-5 flex flex-col gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                  <FiFileText size={18} />
                </div>
                <div>
                  <p className="font-['Inter'] font-bold text-[10px] leading-none uppercase text-[#71717A]">
                    Event
                  </p>
                  <p className="mt-1.5 font-['Inter'] font-medium text-[12px] leading-normal truncate">
                    {snapshot.title || 'Untitled Event'}
                  </p>
                </div>
                {whenLabel && (
                  <div>
                    <p className="font-['Inter'] font-bold text-[10px] leading-none uppercase text-[#71717A]">
                      Date and time
                    </p>
                    <p className="mt-1.5 font-['Inter'] font-medium text-[12px] leading-normal">
                      {whenLabel}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 w-full grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={goToEvent}
                className="h-[42px] rounded-[60px] bg-white px-6 py-[14px] gap-2 text-[#212121] font-['Outfit'] font-semibold text-[14px] leading-none flex items-center justify-center hover:bg-zinc-200 transition-colors cursor-pointer"
              >
                View Event <FiArrowRight size={16} />
              </button>
              <button
                onClick={() => setShareOpen(true)}
                className="h-[42px] rounded-[60px] border border-white/10 bg-zinc-900 px-6 py-[14px] gap-2 font-['Outfit'] font-semibold text-[14px] leading-none flex items-center justify-center hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <FiShare2 size={16} className="text-blue-400" /> Share Event
              </button>
            </div>
          </section>

          {/* Right: next steps */}
          <section className="rounded-[2rem] border border-white/10 bg-zinc-950 overflow-hidden">
            <div className="px-6 py-5 border-b border-white/10">
              <h2 className="font-['Funnel_Display'] font-medium text-[24px] leading-none">
                Next Steps
              </h2>
              <p className="mt-2 font-['Outfit'] text-[14px] leading-none text-[#A3A3A3]">
                Start planning or hiring for your event
              </p>
            </div>
            <div className="p-5 flex flex-col gap-4">
              {!isExternal && (
                <NextStepCard
                  title="Plan your event"
                  description="Manage your budget, assign tasks, collaborate with your team, and keep your event on track."
                  image={planBcg}
                  action={
                    <button
                      onClick={goToProject}
                      className="h-[42px] rounded-[60px] bg-white px-6 py-[14px] gap-2 text-[#212121] font-['Outfit'] font-semibold text-[14px] leading-none flex items-center justify-center hover:bg-zinc-200 transition-colors cursor-pointer"
                    >
                      Open Project <FiArrowRight size={16} />
                    </button>
                  }
                />
              )}
              <NextStepCard
                title="Find your event crew"
                description="Create a gig to hire DJs, photographers, security, ushers, MCs, videographers, and more."
                image={crewBcg}
                action={
                  <button
                    onClick={goToCreateGig}
                    className="h-[42px] rounded-[60px] bg-white px-6 py-[14px] gap-2 text-[#212121] font-['Outfit'] font-semibold text-[14px] leading-none flex items-center justify-center hover:bg-zinc-200 transition-colors cursor-pointer"
                  >
                    <FiPlusCircle size={16} /> Create Gig
                  </button>
                }
              />
              {isPrivate && (
                <div className="border-t border-white/10 pt-5">
                  <h3 className="font-['Funnel_Display'] font-bold text-[18px] leading-none">
                    Guest list
                  </h3>
                  <p className="mt-3 font-['Outfit'] text-[14px] leading-snug text-[#A3A3A3]">
                    Your event is private. Invite guests so they can get access.
                  </p>
                  <button
                    onClick={() => setGuestsOpen(true)}
                    className="mt-4 h-[42px] rounded-[60px] bg-white px-6 py-[14px] gap-2 text-[#212121] font-['Outfit'] font-semibold text-[14px] leading-none flex items-center justify-center hover:bg-zinc-200 transition-colors cursor-pointer"
                  >
                    <FiUsers size={16} /> Invite Guests
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>

      {guestsOpen && (
        <ManagePrivateAccessModal
          eventId={snapshot.eventId}
          isOpen={guestsOpen}
          onClose={() => setGuestsOpen(false)}
        />
      )}

      <ShareEventModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        shareUrl={shareUrl}
        shareTitle={shareTitle}
      />
      <BottomNav />
    </div>
  );
}

function NextStepCard({
  title,
  description,
  action,
  image,
}: {
  title: string;
  description: string;
  action: React.ReactNode;
  image: string;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0b0614] p-5 min-h-40">
      {/* purple glow + starfield */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(124,58,237,0.45),transparent_60%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60 bg-[radial-gradient(1px_1px_at_20%_70%,#fff_50%,transparent_51%),radial-gradient(1px_1px_at_55%_85%,#c4b5fd_50%,transparent_51%),radial-gradient(1.5px_1.5px_at_75%_60%,#a78bfa_50%,transparent_51%),radial-gradient(1px_1px_at_35%_90%,#fff_50%,transparent_51%)]"
      />
      <div className="relative max-w-[65%] sm:max-w-[70%]">
        <h3 className="font-['Funnel_Display'] font-bold text-[18px] leading-none">{title}</h3>
        <p className="mt-3 font-['Outfit'] text-[14px] leading-snug text-[#A3A3A3]">
          {description}
        </p>
        <div className="mt-5 w-fit">{action}</div>
      </div>
      <img
        src={image}
        alt=""
        aria-hidden
        className="absolute -bottom-2 -right-2 w-32 sm:w-40 pointer-events-none select-none"
      />
    </div>
  );
}
